import { Check, CheckCheck, ExternalLink, PencilLine, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import { SocialPostPreviews } from "../../components/flows/SocialPostPreview";
import {
	btn,
	ConfirmDialog,
	Empty,
	ErrorBanner,
	fmtRelative,
	input,
	Panel,
	Spinner,
} from "../../components/flows/ui";
import type { ApprovalItem, FieldChange } from "../../types/flows";

// A run waiting here holds no resources and waits indefinitely, so the thing
// worth saying loudly is that deciding is what resumes it — and that a run only
// resumes once every item on its approval node has been decided.

/**
 * The change, not the thing being changed.
 *
 * A row that says only "Japcell Lithium CR2032 Batterier" asks someone to approve a product; what
 * they can actually judge is the copy that will replace what is there now. Lengths are shown
 * because every field here has a limit Google enforces by truncation.
 */
const Changes = ({ changes }: { changes: FieldChange[] }) => (
	<div className="mt-2 space-y-2">
		{changes.map((change) => (
			<div
				key={change.field}
				className="rounded-lg border border-slate-200 bg-slate-50/60 p-2"
			>
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
						{change.field}
					</span>
					<span className="font-mono text-[10px] text-slate-400 tabular-nums">
						{change.before === null
							? `${change.after?.length ?? 0} chars`
							: `${change.before.length} → ${change.after?.length ?? 0} chars`}
					</span>
				</div>
				<p className="mt-1.5 border-l-2 border-slate-200 pl-2 text-[12px] leading-relaxed text-slate-400">
					{change.before ?? <em>nothing there yet</em>}
				</p>
				<p className="mt-1 border-l-2 border-success pl-2 text-[12px] leading-relaxed text-slate-700">
					{change.after}
				</p>
			</div>
		))}
	</div>
);

interface NodeBatch {
	key: string;
	runId: number;
	nodeId: string;
	flowId: number;
	flowName: string;
	items: ApprovalItem[];
}

const batchesOf = (items: ApprovalItem[]): NodeBatch[] => {
	const groups = new Map<string, NodeBatch>();
	for (const item of items) {
		const key = `${item.runId}:${item.nodeId}`;
		const existing = groups.get(key);
		if (existing) existing.items.push(item);
		else
			groups.set(key, {
				key,
				runId: item.runId,
				nodeId: item.nodeId,
				flowId: item.flowId,
				flowName: item.flowName,
				items: [item],
			});
	}
	return [...groups.values()];
};

const FlowsApprovals = () => {
	const [items, setItems] = useState<ApprovalItem[]>([]);
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [note, setNote] = useState("");
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [approvingAll, setApprovingAll] = useState<NodeBatch | null>(null);

	const load = useCallback(async () => {
		setError(null);
		try {
			setItems(await fetchJson<ApprovalItem[]>("/flows/approvals"));
			setSelected(new Set());
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const canRevise =
		selected.size > 0 &&
		items.every(
			(item) => !selected.has(item.id) || item.preview?.revisable === true,
		);

	const decide = async (decision: "approved" | "rejected" | "revise") => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const result = await sendJson<{
				decided: number;
				resumed: number[];
				error?: string;
			}>("/flows/approvals/decide", "POST", {
				ids: [...selected],
				decision,
				note: note.trim() || undefined,
			});
			if (result.error) {
				setError(result.error);
				return;
			}
			setNote("");
			setNotice(
				result.resumed.length > 0
					? `${result.decided} decided — ${result.resumed.length} run${
							result.resumed.length === 1 ? "" : "s"
						} resumed (#${result.resumed.join(", #")}).`
					: `${result.decided} decided. No run resumed yet — a run only continues once every item on its approval node has been decided.`,
			);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const approveAll = async (batch: NodeBatch) => {
		setBusy(true);
		setError(null);
		setNotice(null);
		try {
			const result = await sendJson<{
				approved: number;
				resumed: boolean;
				error?: string;
			}>("/flows/approvals/approve-all", "POST", {
				runId: batch.runId,
				nodeId: batch.nodeId,
				note: note.trim() || undefined,
			});
			if (result.error) {
				setError(result.error);
				return;
			}
			setApprovingAll(null);
			setNote("");
			setNotice(
				`${result.approved} item${result.approved === 1 ? "" : "s"} approved on run #${batch.runId}.${
					result.resumed
						? " The run has resumed."
						: " The run has not resumed — something on that node is still undecided."
				}`,
			);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	if (loading) return <Spinner />;

	const batches = batchesOf(items);

	return (
		<div className="space-y-4">
			<ErrorBanner error={error} />
			{notice && (
				<p className="rounded-lg bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
					{notice}
				</p>
			)}

			<Panel
				title="Waiting for a decision"
				hint={`${items.length} item${items.length === 1 ? "" : "s"}`}
				action={
					items.length > 0 ? (
						<label className="flex items-center gap-2 text-[12px] text-slate-500">
							<input
								type="checkbox"
								checked={selected.size === items.length && items.length > 0}
								onChange={(event) =>
									setSelected(
										event.target.checked
											? new Set(items.map((i) => i.id))
											: new Set(),
									)
								}
								className="h-4 w-4 rounded border-slate-300 text-primary"
							/>
							Select all
						</label>
					) : undefined
				}
			>
				{items.length === 0 ? (
					<Empty>
						Nothing is waiting. An Approval node pauses its run and puts every
						item here with a preview; approving resumes the run carrying only
						the approved ones.
					</Empty>
				) : (
					<div className="space-y-5">
						{batches.map((batch) => (
							<div key={batch.key} className="space-y-2">
								<div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
									<Link
										to={`/flows/${batch.flowId}`}
										className="text-[12px] font-medium text-slate-700"
									>
										{batch.flowName}
									</Link>
									<Link
										to={`/flows/runs/${batch.runId}`}
										className="text-[12px] text-primary"
									>
										run #{batch.runId}
									</Link>
									<span className="text-[11px] text-slate-400">
										· node {batch.nodeId} · {batch.items.length} item
										{batch.items.length === 1 ? "" : "s"}
									</span>
									<button
										type="button"
										disabled={busy}
										onClick={() => setApprovingAll(batch)}
										title="Approve every pending item on this node in one go, not only the ones ticked."
										className={`${btn.ghost} ml-auto`}
									>
										<CheckCheck className="h-4 w-4" /> Approve all{" "}
										{batch.items.length} on this node
									</button>
								</div>

								{batch.items.map((item) => (
									<div
										key={item.id}
										className="flex items-start gap-3 rounded-xl border border-slate-200 p-3"
									>
										<input
											type="checkbox"
											checked={selected.has(item.id)}
											onChange={(event) => {
												const next = new Set(selected);
												if (event.target.checked) next.add(item.id);
												else next.delete(item.id);
												setSelected(next);
											}}
											className="mt-1 h-4 w-4 rounded border-slate-300 text-primary"
										/>
										<div className="min-w-0 flex-1">
											<p className="text-[13px] font-medium text-slate-800">
												{item.preview?.title ?? "Untitled"}
											</p>
											{item.preview?.warnings &&
												item.preview.warnings.length > 0 && (
													<ul className="mt-1 space-y-0.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12px] text-amber-800">
														{item.preview.warnings.map((warning) => (
															<li key={warning}>{warning}</li>
														))}
													</ul>
												)}
											{item.social && item.social.length > 0 ? (
												<SocialPostPreviews posts={item.social} />
											) : item.preview?.changes &&
												item.preview.changes.length > 0 ? (
												<Changes changes={item.preview.changes} />
											) : (
												item.preview?.summary && (
													<p className="mt-1 line-clamp-3 text-[12px] leading-relaxed text-slate-500">
														{item.preview.summary}
													</p>
												)
											)}
											<div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
												<Link
													to={`/flows/${item.flowId}`}
													className="text-slate-600"
												>
													{item.flowName}
												</Link>
												<Link
													to={`/flows/runs/${item.runId}`}
													className="text-primary"
												>
													run #{item.runId}
												</Link>
												<span>· item {item.itemIndex + 1}</span>
												<span>· waiting {fmtRelative(item.createdAt)}</span>
												{item.preview?.links.map((link) => (
													<a
														key={link.url}
														href={link.url}
														target="_blank"
														rel="noreferrer"
														className="inline-flex items-center gap-1 font-medium text-primary"
													>
														{link.label} <ExternalLink className="h-3 w-3" />
													</a>
												))}
											</div>
										</div>
									</div>
								))}
							</div>
						))}
					</div>
				)}
			</Panel>

			{items.length > 0 && (
				<Panel>
					<div className="flex flex-wrap items-end gap-3">
						<label className="block min-w-64 flex-1 space-y-1">
							<span className="block text-[12px] font-medium text-slate-600">
								Note (optional; for Revise, what should change)
							</span>
							<input
								className={input}
								placeholder="Why — recorded against every item you decide. Revise gives the rewrite exactly this."
								value={note}
								onChange={(event) => setNote(event.target.value)}
							/>
						</label>
						<button
							type="button"
							disabled={busy || selected.size === 0}
							onClick={() => decide("approved")}
							className={btn.primary}
						>
							<Check className="h-4 w-4" /> Approve {selected.size || ""}
						</button>
						<button
							type="button"
							disabled={busy || selected.size === 0}
							onClick={() => decide("rejected")}
							className={btn.danger}
						>
							<X className="h-4 w-4" /> Reject {selected.size || ""}
						</button>
						<button
							type="button"
							disabled={busy || !canRevise || note.trim() === ""}
							onClick={() => decide("revise")}
							title={
								!canRevise
									? "Only comparison pages drafted from the candidate queue can be revised"
									: note.trim() === ""
										? "Write in the note what should change"
										: "Not published now — rewritten by the next drafting run"
							}
							className={btn.ghost}
						>
							<PencilLine className="h-4 w-4" /> Revise {selected.size || ""}
						</button>
					</div>
					<p className="mt-3 text-[11px] leading-relaxed text-slate-400">
						Approving resumes the run from the approval node with only the
						approved items. A rejected item is dropped and remembered, so a
						later run does not propose it again. A revised item is not published
						now: its pair goes back to the Comparisons queue with your note, and
						the next drafting run rewrites the page it already has rather than
						skipping it.
					</p>
				</Panel>
			)}

			{approvingAll && (
				<ConfirmDialog
					title={`Approve all ${approvingAll.items.length} items on this node`}
					busy={busy}
					confirmLabel={`Approve ${approvingAll.items.length}`}
					onCancel={() => setApprovingAll(null)}
					onConfirm={() => approveAll(approvingAll)}
					body={
						<>
							<p>
								This approves{" "}
								<strong>
									every one of the {approvingAll.items.length} pending items
								</strong>{" "}
								on node {approvingAll.nodeId} of run #{approvingAll.runId} — not
								only the ones ticked, and including any that arrived since this
								page last loaded.
							</p>
							<p>
								{approvingAll.flowName} will then resume from the approval node
								and carry all of them into whatever comes next, which for an SEO
								flow is the write. This is the control for draining a large
								focus-tier batch; use the checkboxes when you want to judge
								items one by one.
							</p>
							{note.trim() !== "" && (
								<p>
									The note “{note.trim()}” is recorded against each of them.
								</p>
							)}
						</>
					}
				/>
			)}
		</div>
	);
};

export default FlowsApprovals;
